import { Button } from '@hologram-dimension/button';
import { Checkbox } from '@hologram-dimension/checkbox';
import { CheckboxField } from '@hologram-dimension/checkbox-field';
import { DropdownPanel } from '@hologram-dimension/dropdown-panel';
import { FormField } from '@hologram-dimension/form-field';
import { Icon } from '@hologram-dimension/icon';
import useTags from 'api/tags/useTags';
import { type FC, useCallback, useMemo, useState } from 'react';
import { type UseFormRegister, UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import useOrganization from 'state/contexts/Organization/useOrganization';
import styles from './SimRecentSessionsOrganization.module.css';

interface SimRecentSessionsOrganizationProps {
  refreshSessions: () => void;
  register: UseFormRegister<TagSelectFormFields>;
  watch: UseFormWatch<TagSelectFormFields>;
  setValue: UseFormSetValue<TagSelectFormFields>;
}

export interface TagSelectFormFields {
  tags: Array<string>;
  selectAllTags: string;
}

const SimRecentSessionsOrganization: FC<SimRecentSessionsOrganizationProps> = ({
  refreshSessions,
  register,
  watch,
  setValue,
}) => {
  const organizationId = useOrganization();
  const [open, setOpen] = useState(false);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const {
    tags,
    isLoading: tagsLoading,
    error: tagsError,
  } = useTags({
    filters: organizationId ? { organizationIds: [organizationId] } : undefined,
  });

  const { selectAllTags, tags: selectedTags } = watch();

  const selectedTagsLookup = useMemo(() => new Set(selectedTags), [selectedTags]);

  const buttonText = useMemo(() => {
    let tagsToUse;
    if (selectAllTags === '1') {
      tagsToUse = tags;
    } else if (selectedTagsLookup.size > 0) {
      tagsToUse = tags.filter((tag) => selectedTagsLookup.has(`${tag.id}`));
    }

    if (!tagsToUse || tagsToUse.length === 0) {
      return 'No tags selected';
    }

    return `(${tagsToUse.length}) ${tagsToUse.map((tag) => tag.name).join(', ')}`;
  }, [selectAllTags, selectedTagsLookup, tags]);

  if (tagsLoading || tagsError) {
    if (tagsError) {
      // eslint-disable-next-line no-console
      console.error(tagsError);
    }

    return null;
  }

  return (
    <div className={styles.wrapper}>
      <FormField label="Tags">
        {({ ariaDescribedbyIds, ariaLabel, primaryControlId }) => (
          <div>
            {/* This is necessary so that dimension correctly applies block end styling to only this element */}
            <DropdownPanel
              className={styles.dropdown}
              content={
                <div className={styles.tagsDropdown}>
                  <Checkbox
                    key="select-all"
                    label="Select all"
                    value="1"
                    className={styles.selectAllCheckbox}
                    indeterminate={
                      (!selectAllTags &&
                        (selectedTags?.length ?? 0) > 0 &&
                        selectedTags!.length !== tags?.length) ??
                      0
                    }
                    defaultChecked={selectAllTags === '1'}
                    {...register('selectAllTags', {
                      onChange: (e) => {
                        if (e.target.checked) {
                          setValue(
                            'tags',
                            tags.map((tag) => `${tag.id}`)
                          );
                        } else {
                          setValue('tags', []);
                        }
                      },
                    })}
                  />
                  <CheckboxField
                    label=""
                    options={tags?.map((tag) => ({
                      labelNode: (
                        <div className={styles.checkboxLabel}>
                          <span>{tag.name}</span>
                          <span className={styles.simCount}>({tag.simCount})</span>
                        </div>
                      ),
                      label: `${tag.name} (${tag.simCount})`,
                      value: `${tag.id}`,
                      defaultChecked: !!tag.id && selectedTagsLookup.has(`${tag.id}`),
                    }))}
                    {...register('tags', {
                      onChange: () => {
                        if (selectAllTags === '1') {
                          setValue('selectAllTags', false as any);
                        }
                      },
                    })}
                  />
                </div>
              }
              open={open}
              onOpenChange={setOpen}
            >
              <Button
                id={primaryControlId}
                aria-describedby={ariaDescribedbyIds}
                aria-label={ariaLabel}
                variant="secondary"
                className={styles.tagsButton}
                onClick={toggleOpen}
                iconEnd={
                  <Icon name={open ? 'ChevronSingleNorth' : 'ChevronSingleSouth'} size="small" />
                }
              >
                {buttonText}
              </Button>
            </DropdownPanel>
          </div>
        )}
      </FormField>
      <Button onClick={refreshSessions}>Refresh sessions</Button>
    </div>
  );
};

export default SimRecentSessionsOrganization;
